import type { ReactNode } from 'react';
import { refreshOutline, thumbsDown, thumbsUp } from 'ionicons/icons';
import IonIcon from '../components/IonIcon';
import FloatingFooter, {
  FloatingFooterCta,
  FloatingFooterNavButton,
} from '../components/FloatingFooter';
import { isStepNo, isStepResolved, stepComment } from '../runEvidence';
import type { WebRunStep } from '../runTypes';

interface Props {
  step: WebRunStep;
  /** Owner reviewing a delegated run: see evidence, no Yes/No/reopen. */
  readOnly?: boolean;
  sending: boolean;
  sendError: string | null;
  onChoose: (outcome: 'yes' | 'no') => void;
  onReopen: () => void;
  onEditNote: () => void;
  /** Null when the failed action is no longer queued — nothing left to retry. */
  onRetry: (() => void) | null;
  onClose: () => void;
}

function OutcomeBadge({ step }: { step: WebRunStep }) {
  const no = isStepNo(step);
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${
        no ? 'bg-x9-error text-white' : 'bg-x9-cyan text-x9-marine'
      }`}
    >
      <IonIcon icon={no ? thumbsDown : thumbsUp} size={14} filled />
      {no ? 'No — issue reported' : 'Yes, all good'}
    </span>
  );
}

function SheetFooter({
  onBack,
  sending,
  children,
}: {
  onBack: () => void;
  sending?: boolean;
  children?: ReactNode;
}) {
  return (
    <FloatingFooter>
      <FloatingFooterNavButton label="Back" onClick={onBack} disabled={sending} />
      {children}
    </FloatingFooter>
  );
}

export default function RunStepSheet({
  step,
  readOnly = false,
  sending,
  sendError,
  onChoose,
  onReopen,
  onEditNote,
  onRetry,
  onClose,
}: Props) {
  const resolved = isStepResolved(step);
  const comment = stepComment(step);
  const proofs = step.proofUrls || [];
  const footer = (children?: ReactNode) => (
    <SheetFooter onBack={onClose} sending={sending}>
      {children}
    </SheetFooter>
  );

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-white">
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-32 pt-10">
        {step.photoUrl ? (
          <img
            src={step.photoUrl}
            alt=""
            className="mb-4 h-36 w-full rounded-2xl object-cover"
          />
        ) : null}
        <p className="text-xs font-medium uppercase tracking-wide text-x9-marineSoft">
          {step.sectionName || 'Step'}
        </p>
        <h2 className="mt-1 text-xl font-semibold text-x9-ink">{step.title}</h2>
        {step.description ? (
          <p className="mt-2 text-sm leading-relaxed text-x9-marineSoft">{step.description}</p>
        ) : null}

        {resolved ? (
          <div className="mt-5 space-y-4">
            <OutcomeBadge step={step} />
            {readOnly ? (
              comment ? (
                <div className="rounded-2xl border border-x9-line bg-x9-paper px-4 py-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-x9-marineSoft">
                    Comment
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-x9-ink">{comment}</p>
                </div>
              ) : null
            ) : (
              <button
                type="button"
                onClick={onEditNote}
                className="block w-full rounded-2xl border border-x9-line bg-x9-paper px-4 py-3 text-left"
              >
                <p className="text-xs font-medium uppercase tracking-wide text-x9-marineSoft">
                  {comment ? 'Your comment' : 'Comment'}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-x9-ink">
                  {comment || 'Tap to add a comment'}
                </p>
              </button>
            )}
            {proofs.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {proofs.map((url) => (
                  <img
                    key={url}
                    src={url}
                    alt=""
                    className="size-16 rounded-[5px] object-cover"
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {sendError ? (
          <div className="mt-6 rounded-xl bg-x9-paper px-4 py-3 text-sm text-x9-ink">
            <p>{sendError}</p>
            {onRetry ? (
              <button type="button" className="mt-2 font-medium text-x9-marine" onClick={onRetry}>
                Retry
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {readOnly ? (
        footer()
      ) : resolved ? (
        footer(
          <FloatingFooterCta tone="muted" disabled={sending} onClick={onReopen}>
            <IonIcon icon={refreshOutline} size={18} />
            {sending ? 'Reopening…' : 'Reopen'}
          </FloatingFooterCta>,
        )
      ) : (
        footer(
          <>
            <button
              type="button"
              disabled={sending}
              aria-label="No"
              onClick={() => onChoose('no')}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] border border-x9-error bg-white text-x9-error disabled:opacity-45"
            >
              <IonIcon icon={thumbsDown} size={20} filled />
            </button>
            <FloatingFooterCta disabled={sending} onClick={() => onChoose('yes')}>
              <IonIcon icon={thumbsUp} size={18} filled />
              {sending ? 'Sending…' : 'Yes, all good'}
            </FloatingFooterCta>
          </>,
        )
      )}
    </div>
  );
}
